import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { PATHS } from '@belong/common';
import { Spinner, Text } from '@belong/ui';
import { getUnitsFromProperties } from 'accounts/utils/units';
import { AutoCompleteFinalFormAdapter } from 'components/AutoComplete/AutoComplete';
import Field from 'components/Field/Field';
import Form from 'components/Form/Form';
import { InputFinalFormAdapter } from 'components/Input/Input';
import {
  MEDIA_LABEL,
  NewMediaUploadAdapter,
  validateMedia,
} from 'components/NewMediaUploader/NewMediaUploader';
import {
  SELECTOR_TYPES,
  SelectorFinalFormAdapter,
} from 'components/Selector/Selector';
import String from 'components/String/String';
import MaintenanceModalAttachmentFields from 'containercomponents/Modals/AddTaskModal/MaintenanceModalAttachmentFields';
import {
  listOfAppliances,
  responsibility,
} from 'containercomponents/Modals/ModalListOfItems/ApplianceList/listOfFormObjects';
import arrayMutators from 'final-form-arrays';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { URL_QUERY_PARAMS } from 'maintenance/constants';
import { STRINGS } from 'maintenance/strings';
import type { EditProServiceRouteParams } from 'maintenance/types';
import { upgradesNames, validateMaintenanceSummary } from 'maintenance/utils';
import { RequestType } from 'models/enums/index';
import HomeownerAccountProperty from 'models/homeownerAccounts/HomeownerAccountProperty';
import HaulingForm from 'pages/Accounts/FormLayouts/HaulingForm/HaulingForm';
import KeysForm from 'pages/Accounts/FormLayouts/KeysForm/KeysForm';
import ResidentAvailabilityForm from 'pages/Accounts/FormLayouts/ResidentAvailabilityForm/ResidentAvailabilityForm';
import {
  createMaintenanceTask,
  updateMaintenanceTask,
  fetchHOTaskByTaskId,
  maintenanceCurrentClear,
} from 'store/redux/maintenance/actions';
import { selectCurrentTask } from 'store/redux/maintenance/selectors';
import { clearToasts } from 'store/redux/toast';
import { ADD_TASK_MODAL_STRINGS } from 'strings/add-task-modal';
import { required } from 'utils/validation';

type Props = {
  isHomeOwnerView: boolean;
  proService: string;
  properties: HomeownerAccountProperty[];
  // Structural rather than `UnitBasicInfoModel`: the caller passes
  // `currentLease.unitInfo`, an instance of the `UnitBasicInfo` class, not the
  // generated interface. `unitId` is the only field this form reads.
  unit?: { unitId?: string };
};

const descriptionMinCharacters = 150;

export function HomeServiceForm({
  isHomeOwnerView,
  proService,
  properties,
  unit,
}: Props) {
  const { taskId } = useParams<EditProServiceRouteParams>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { search } = useLocation();
  const task = useSelector(selectCurrentTask);
  const externalUnitId = new URLSearchParams(search).get(
    URL_QUERY_PARAMS.UNIT_ID,
  );
  const units = getUnitsFromProperties(properties);

  // `basicInfo` and `property.address` are optional on the generated models but
  // always present on a full-management home, and each read below already
  // dereferenced them unconditionally. The assertions are erased at runtime.
  const homes = units
    .map((homeUnit) => {
      const formattedAddress = homeUnit.basicInfo!.unitNumber
        ? `${homeUnit.property.address!.streetAddress}, ${STRINGS.unit} ${homeUnit.basicInfo!.unitNumber}`
        : homeUnit.property.address!.streetAddress;

      return {
        key: homeUnit.basicInfo!.unitId,
        value: formattedAddress,
        streetAddress: homeUnit.property.address!.streetAddress,
      };
    })
    .sort((firstHome, secondHome) => {
      return firstHome.streetAddress!.localeCompare(
        secondHome.streetAddress!,
        undefined,
        {
          numeric: true,
          sensitivity: 'base',
        },
      );
    });

  const firstHome = homes?.length === 1 ? homes[0] : null;
  const defaultHome = isHomeOwnerView ? firstHome : unit?.unitId;
  const home = externalUnitId || defaultHome;

  const getInitialValues = () => {
    const renterObj: Record<string, unknown> = {};

    if (!isHomeOwnerView) {
      renterObj.doesResidentClaimResponsibility =
        task.doesResidentClaimResponsibility?.toString() || null;
      renterObj.requestorAvailability = task.requestorAvailability || [];
      renterObj.summary = taskId
        ? task.summary
        : // Cast because `upgradesNames` is an object literal, so only its own
          // eight keys can index it; the `?.` already handles a miss.
          upgradesNames[proService.toLowerCase() as keyof typeof upgradesNames]
            ?.label;
    }

    if (taskId) {
      const filteredMedia = task.media.filter(
        (m: any) => m.stage === 'Before' && !m.subOwner,
      );

      return {
        ...task,
        description: task.description || '',
        home: task.homeUniqueId,
        issueType: proService,
        attachments: {
          availableMedia: filteredMedia,
          mediaToDisplay: filteredMedia,
          deletedMedia: [],
          later: !filteredMedia?.length,
        },
        doesResidentClaimResponsibility:
          task.isResidentFault?.toString() || 'false',
        ...renterObj,
      };
    }

    const initialValues: Record<string, unknown> = {
      home,
      attachments: {
        availableMedia: [],
        deletedMedia: [],
      },
      issueType: proService,
      ...renterObj,
    };

    if (proService.toLowerCase() === 'haulingandstorage') {
      initialValues.requestTypeDetails = {
        type: 'Haul',
        items: [{}],
      };
    }

    return initialValues;
  };

  async function handleFormSubmit(values: Record<string, unknown>) {
    const submitValues: Record<string, unknown> = {
      homeUniqueId: home,
      requestType: values.issueType || proService,
      ...values,
    };

    if (!isHomeOwnerView) {
      const getOnlyValidDates = (
        values.requestorAvailability as Array<{ times?: string[] }>
      ).filter((date) => {
        return date.times?.length;
      });
      const validDates = getOnlyValidDates.length ? getOnlyValidDates : null;

      submitValues.requestorAvailability = values.isRequestorPresent
        ? validDates
        : null;

      if (Object.keys(upgradesNames).includes(proService.toLowerCase())) {
        submitValues.requestType = 'Upgrade';
      }
    }

    if (submitValues.requestType === RequestType.HaulingAndStorage) {
      submitValues.summary = 'Item removal';
    }

    delete submitValues.home;

    setIsSubmitting(true);

    try {
      dispatch(clearToasts());

      const urlSearchParams = new URLSearchParams();

      urlSearchParams.append(URL_QUERY_PARAMS.TOAST_VISIBLE, 'true');
      urlSearchParams.append(URL_QUERY_PARAMS.SUCCESS, 'true');

      if (!taskId) {
        await dispatch(createMaintenanceTask(submitValues));
        urlSearchParams.append(URL_QUERY_PARAMS.CREATION, 'true');
      } else {
        await dispatch(updateMaintenanceTask(submitValues, taskId));
        urlSearchParams.append(URL_QUERY_PARAMS.UPDATE, 'true');
      }

      push({
        pathname: isHomeOwnerView
          ? PATHS.HOMEOWNER_ACCOUNT_MAINTENANCE
          : PATHS.RESIDENTS_ACCOUNT_MAINTENANCE,
        search: urlSearchParams.toString(),
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    const fetchTask = async () => {
      try {
        setIsLoading(true);
        await dispatch(fetchHOTaskByTaskId(taskId));
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (taskId) {
      fetchTask();
    }

    return () => {
      dispatch(maintenanceCurrentClear());
    };
  }, [dispatch, taskId]);

  return (
    <>
      {(isSubmitting || isLoading) && <Spinner />}
      <Form
        onSubmit={handleFormSubmit}
        initialValues={getInitialValues()}
        mutators={{
          ...arrayMutators,
        }}
        getFormBottomBar={(formProps: any, nextButtonProps: any) => {
          return (
            <div className="mb-3xl flex items-center justify-center">
              <FormLayout.Button
                buttonClassNames="w-button-fluid sm:w-button-default h-button flex justify-center"
                ctaProps={{
                  label: 'Submit',
                }}
                nextButtonWrapperProps={nextButtonProps}
              />
            </div>
          );
        }}
        bottomPosition="25px"
        getForm={({ form, handleSubmit, values }) => {
          const descriptionCharactersLeft = values.description
            ? descriptionMinCharacters - values.description.length
            : descriptionMinCharacters;
          return (
            <form onSubmit={handleSubmit}>
              <FormLayout>
                {values.issueType === RequestType.Repair && (
                  <>
                    <FormLayout.Section
                      sectionTitle={ADD_TASK_MODAL_STRINGS.repair.title}
                    >
                      <Field
                        name="summary"
                        validate={validateMaintenanceSummary}
                        allowUserInput
                        component={AutoCompleteFinalFormAdapter}
                        items={listOfAppliances}
                        placeholder={
                          ADD_TASK_MODAL_STRINGS.appliance_placeholder
                        }
                      />
                    </FormLayout.Section>

                    <FormLayout.Section
                      sectionTitle={
                        <String
                          string={
                            ADD_TASK_MODAL_STRINGS.repair.describe_issue_title
                          }
                        />
                      }
                      sectionSubTitle={
                        <String
                          string={
                            ADD_TASK_MODAL_STRINGS.repair
                              .describe_issue_subtitle
                          }
                        />
                      }
                    >
                      <Field
                        name="description"
                        component={InputFinalFormAdapter}
                        placeholder={
                          ADD_TASK_MODAL_STRINGS.repair
                            .describe_issue_placeholder
                        }
                        validate={required}
                        textarea
                      />
                      <Text className="mt-sm text-right">
                        {descriptionCharactersLeft <= 0 && (
                          <Text
                            className="text-green"
                            fontWeight="semibold"
                            variant="p1"
                          >
                            Excellent!
                          </Text>
                        )}
                        {descriptionCharactersLeft > 0 &&
                          descriptionCharactersLeft !==
                            descriptionMinCharacters && (
                            <Text
                              className="text-red"
                              fontWeight="semibold"
                              variant="p1"
                            >
                              More Info Recommended
                            </Text>
                          )}
                      </Text>
                    </FormLayout.Section>
                    {!isHomeOwnerView && (
                      <div className="relative">
                        <FormLayout.Section
                          sectionTitle={
                            ADD_TASK_MODAL_STRINGS[
                              'renter_repair_modal.responsibility_title'
                            ]
                          }
                          sectionSubTitle={
                            <String
                              string={
                                ADD_TASK_MODAL_STRINGS[
                                  'renter_repair_modal.responsibility_subtitle'
                                ]
                              }
                            />
                          }
                        >
                          <Field
                            name="doesResidentClaimResponsibility"
                            component={SelectorFinalFormAdapter}
                            placeholder={
                              ADD_TASK_MODAL_STRINGS.urgency_placeholder
                            }
                            buttons={responsibility}
                            validate={required}
                            type={SELECTOR_TYPES.MEDIUMTEXTBUTTON}
                          />
                        </FormLayout.Section>
                      </div>
                    )}
                    {!isHomeOwnerView && (
                      <ApprovalMessage
                        show={
                          values.doesResidentClaimResponsibility === 'false'
                        }
                      />
                    )}
                    {!isHomeOwnerView && (
                      <ResponsibilityMessage
                        show={values.doesResidentClaimResponsibility === 'true'}
                      />
                    )}

                    <FormLayout.Section
                      sectionTitle={ADD_TASK_MODAL_STRINGS['upload.title']}
                      sectionSubTitle={
                        <String
                          string={
                            ADD_TASK_MODAL_STRINGS['upload.subtitle.repair']
                          }
                        />
                      }
                    >
                      <Field
                        name="attachments"
                        component={NewMediaUploadAdapter}
                        mediaLabel={MEDIA_LABEL.PHOTO_OR_VIDEO}
                        onChangeCustom={(value) => {
                          if (value.stagingMedia.length) {
                            form.change('attachments.later', false);
                          }
                        }}
                        wrapperSize="large"
                        validate={validateMedia}
                        disableWebcam
                      />
                    </FormLayout.Section>
                    {/* <FormLayout.Section sectionTitle={ADD_TASK_MODAL_STRINGS.urgency}>
                      <Field
                        name="urgency"
                        component={SelectorFinalFormAdapter}
                        placeholder={ADD_TASK_MODAL_STRINGS.urgency_placeholder}
                        buttons={urgency}
                        validate={required}
                        type={SELECTOR_TYPES.MEDIUMTEXTBUTTON}
                      />
                    </FormLayout.Section> */}
                    {/* {values.urgency === 'Emergency' && (
                      <div className="flex flex-row p-sm border border-solid border-green rounded bg-green-light-bg -mt-md mb-lg">
                        <Text>
                          <span role="img" aria-label="emergency emoji" className="mr-xs">
                            🚨️
                          </span>
                          <span>{ADD_TASK_MODAL_STRINGS.emergency}</span>
                          <String string={ADD_TASK_MODAL_STRINGS.danger} className="mt-sm" />
                        </Text>
                      </div>
                    )} */}
                    {!isHomeOwnerView && (
                      <ResidentAvailabilityForm
                        isRequestorPresent={values.isRequestorPresent}
                      />
                    )}
                  </>
                )}
                {(values.issueType === RequestType.Upgrade ||
                  Object.keys(upgradesNames).includes(
                    proService.toLowerCase(),
                  )) && (
                  <>
                    <FormLayout.Section
                      sectionTitle={
                        isHomeOwnerView
                          ? ADD_TASK_MODAL_STRINGS.upgrade.title_homeowner
                          : ADD_TASK_MODAL_STRINGS.upgrade.title_resident
                      }
                    >
                      <Field
                        name="summary"
                        validate={validateMaintenanceSummary}
                        allowUserInput
                        component={AutoCompleteFinalFormAdapter}
                        items={listOfAppliances}
                        placeholder={
                          ADD_TASK_MODAL_STRINGS.appliance_placeholder
                        }
                      />
                    </FormLayout.Section>

                    <FormLayout.Section
                      sectionTitle={
                        ADD_TASK_MODAL_STRINGS.upgrade.describe_issue_title
                      }
                      sectionSubTitle={
                        !isHomeOwnerView
                          ? ADD_TASK_MODAL_STRINGS.upgrade
                              .describe_issue_subtitle
                          : undefined
                      }
                    >
                      <Field
                        name="description"
                        component={InputFinalFormAdapter}
                        placeholder={
                          ADD_TASK_MODAL_STRINGS.upgrade
                            .describe_issue_placeholder
                        }
                        validate={required}
                        min={150}
                        textarea
                      />
                      <Text className="mt-sm text-right">
                        {descriptionCharactersLeft <= 0 && (
                          <Text
                            className="text-green"
                            fontWeight="semibold"
                            variant="p1"
                          >
                            Excellent!
                          </Text>
                        )}
                        {descriptionCharactersLeft > 0 &&
                          descriptionCharactersLeft !==
                            descriptionMinCharacters && (
                            <Text
                              className="text-red"
                              fontWeight="semibold"
                              variant="p1"
                            >
                              More Info Recommended
                            </Text>
                          )}
                      </Text>
                    </FormLayout.Section>
                    <FormLayout.Section
                      sectionTitle={ADD_TASK_MODAL_STRINGS['upload.title']}
                      sectionSubTitle={
                        <String
                          string={
                            ADD_TASK_MODAL_STRINGS[
                              'upload.subtitle.upgrade_or_addition'
                            ]
                          }
                        />
                      }
                    >
                      <MaintenanceModalAttachmentFields
                        form={form}
                        showTooltip={false}
                      />
                    </FormLayout.Section>
                    {!isHomeOwnerView && (
                      <>
                        <ResidentAvailabilityForm
                          isRequestorPresent={values.isRequestorPresent}
                        />{' '}
                        <div className="-mt-md mb-lg flex flex-row rounded border border-solid border-gray bg-light-gray p-sm">
                          <Text>{ADD_TASK_MODAL_STRINGS.follow_up}</Text>
                        </div>
                      </>
                    )}
                  </>
                )}
                {proService.toLowerCase() ===
                  RequestType.HaulingAndStorage.toLowerCase() && (
                  <>
                    <HaulingForm form={form} />
                    {!isHomeOwnerView && (
                      <ResidentAvailabilityForm
                        isRequestorPresent={values.isRequestorPresent}
                      />
                    )}
                  </>
                )}
                {proService.toLowerCase() ===
                  RequestType.KeysAndAccess.toLowerCase() && (
                  <>
                    <KeysForm form={form} />
                    {!isHomeOwnerView && (
                      <ResidentAvailabilityForm
                        isRequestorPresent={values.isRequestorPresent}
                      />
                    )}
                  </>
                )}
              </FormLayout>
            </form>
          );
        }}
      />
    </>
  );
}

type ApprovalMessageProps = {
  show?: boolean;
  taskName?: string;
};

const ApprovalMessage = ({ show, taskName }: ApprovalMessageProps) => {
  // Guarded rather than asserted: `taskName` is optional, and indexing with
  // `undefined` looked up the literal `"undefined"` key, which this map never
  // has — so both forms yield `undefined`.
  const requiresApproval = taskName
    ? upgradesNames[taskName as keyof typeof upgradesNames]?.approval
    : undefined;

  if (show || requiresApproval) {
    return (
      <div className="-mt-md mb-lg flex flex-row rounded border border-solid border-gray bg-light-gray p-sm">
        <Text>{ADD_TASK_MODAL_STRINGS.approval_required}</Text>
      </div>
    );
  }

  return null;
};

const ResponsibilityMessage = ({ show }: { show: boolean }) => {
  if (show) {
    return (
      <div className="-mt-md mb-lg flex flex-row rounded border border-solid border-gray bg-light-gray p-sm">
        <Text>
          <span role="img" aria-label="" className="mr-xs">
            ❤️
          </span>
          {ADD_TASK_MODAL_STRINGS.responsibility_accept}
        </Text>
      </div>
    );
  }

  return null;
};
